import { FunctionComponent } from 'preact';
import { useCallback, useEffect } from 'preact/hooks';

import { usePlaybackCountdown } from '../../hooks/usePlaybackCountdown';
import { UpNextLayout } from '../UpNextLayout/UpNextLayout';
import { useNextAsset } from '../../hooks/NextAssetProvider/NextAssetProvider';
import { useUpNextTracking } from '../../hooks/useUpNextTracking';
import { usePlayerEvent } from '../../hooks/usePlayerEvent';
import { useCurrentAsset } from '../../hooks/CurrentAssetProvider';
import { usePlayNext } from '../../hooks/usePlayNext';
import { useTpaasTracker } from '../../hooks/useTpaasTracker';

interface UpNextPreviewLayoutProps {
  onCancelClick: () => void;
}

export const PREVIEW_THRESHOLD_IN_SECONDS = 10;

export const UpNextPreviewLayout: FunctionComponent<
  UpNextPreviewLayoutProps
> = ({ onCancelClick }) => {
  const { trackEngagementUIElement } = useTpaasTracker();
  const { playNext } = usePlayNext();
  const { nextAsset } = useNextAsset();
  const { remainingTime } = usePlaybackCountdown(PREVIEW_THRESHOLD_IN_SECONDS);
  const {
    trackView,
    trackCancelClick,
    trackSkipClick,
    trackComplete,
    trackReplay,
  } = useUpNextTracking('preview');

  const { asset } = useCurrentAsset();

  useEffect(() => {
    trackView();
  }, []);

  const handleCancelClick = useCallback(
    (e: MouseEvent) => {
      trackCancelClick();
      if (e.currentTarget instanceof Element) {
        trackEngagementUIElement({
          elementName: 'Cancel Countdown',
          elementText: e.currentTarget.textContent ?? undefined,
          elementType: e.currentTarget.tagName.toLowerCase(),
          name: 'Cancel',
        });
      }

      onCancelClick();
    },
    [onCancelClick, trackEngagementUIElement]
  );

  const handleSkipClick = useCallback(
    (e: MouseEvent) => {
      trackSkipClick();
      if (e.currentTarget instanceof Element) {
        trackEngagementUIElement({
          elementName: 'PlayNext Countdown',
          elementText: e.currentTarget.textContent ?? undefined,
          elementType: e.currentTarget.tagName.toLowerCase(),
          name: 'PlayNext',
          playEntry: 'Next',
        });
      }

      playNext(nextAsset!, {
        pulse: { playbackSource: 'autoplayNext-manual' },
      });
    },
    [trackEngagementUIElement]
  );

  const handleReplayClick = useCallback(() => {
    trackReplay();

    playNext(asset!, {
      pulse: { playbackSource: 'replay' },
    });
  }, []);

  const handleComplete = useCallback((reason?: string) => {
    if (!reason) {
      trackComplete();
    }
  }, []);

  usePlayerEvent('complete', handleComplete);

  return (
    <UpNextLayout
      onCancelClick={handleCancelClick}
      onSkipClick={handleSkipClick}
      remainingTime={remainingTime}
      countdownDuration={PREVIEW_THRESHOLD_IN_SECONDS}
      handleReplay={handleReplayClick}
    />
  );
};
